import { defineComponent as _defineComponent } from 'vue';
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "font-semibold text-h6" };
const _hoisted_2 = { class: "d-flex align-center gap-3" };
const _hoisted_3 = { class: "text-body-2 mb-2" };
const _hoisted_4 = { class: "d-flex align-center gap-4 mb-4 text-body-2" };
const _hoisted_5 = { class: "font-medium" };
const _hoisted_6 = { class: "space-y-2" };
const _hoisted_7 = { class: "ml-4" };
const _hoisted_8 = {
    key: 0,
    class: "mt-2"
};
const _hoisted_9 = { class: "space-y-2" };
const _hoisted_10 = { class: "mt-2" };
const _hoisted_11 = {
    class: "text-body-2",
    style: { "white-space": "pre-line" }
};
const _hoisted_12 = { class: "text-body-2 text-muted-foreground" };
const _hoisted_13 = { key: 0 };
const _hoisted_14 = { key: 1 };
import { ref, computed, onMounted, onUnmounted, watch, provide, nextTick } from "vue";
import { intakeService } from "@/services/intakeService";
import CriteriaItem from "./PolicyReviewCriteriaItem.vue";
import { decamelizeKeys } from "humps";
const MAX_POLL_ATTEMPTS = 30; // 30 attempts * 10 seconds = 5 minutes max (safety margin)
const POLL_INTERVAL_MS = 10000;
export default /*@__PURE__*/ _defineComponent({
    __name: 'PolicyReviewCriteriaCard',
    props: {
        order: {},
        currentPageId: {},
        pageManager: {}
    },
    setup(__props) {
        const props = __props;
        // Initialize with a safe default structure to prevent undefined status errors
        const policyReview = ref({
            status: "processing",
            message: "Policy review is running",
        });
        const loading = ref(false);
        const criteriaTree = ref([]);
        const renderKey = ref(0);
        const criteriaState = ref({}); // Store user-modified statuses
        const pageAttachments = ref({}); // Store page attachments
        const retryingPolicyReview = ref(false);
        // Helper to get page attachments for a criterion id (handles undefined)
        const getAttachedPages = (id) => {
            if (!id)
                return [];
            return pageAttachments.value[id] || [];
        };
        // Polling timeout and retry management
        // Policy review typically takes 3~4 minutes, so we allow up to 5 minutes
        let pollInterval = null;
        let pollAttempts = 0;
        const isProcessing = computed(() => {
            // Don't show processing if we have data with criteria
            if (criteriaTree.value.length > 0) {
                return false;
            }
            // Safely check status with optional chaining
            const status = policyReview.value?.status;
            return status === "processing" || loading.value;
        });
        // Check if we can retry/trigger policy review
        const canRetryPolicyReview = computed(() => {
            // Safely check status with optional chaining
            return policyReview.value?.status === "processing" || policyReview.value?.status === "completed" || hasError.value;
        });
        const hasError = computed(() => {
            // Check for failed status OR completed status with an error field
            // Safely check status with optional chaining
            const status = policyReview.value?.status;
            const error = policyReview.value?.error;
            return status === "failed" || (status === "completed" && !!error);
        });
        const errorMessage = computed(() => {
            return policyReview.value?.error || policyReview.value?.message || "An unknown error occurred during policy review.";
        });
        const shouldShow = computed(() => {
            if (!policyReview.value || typeof policyReview.value !== "object") {
                return isProcessing.value || criteriaTree.value.length > 0;
            }
            const structuredOutput = policyReview.value?.structuredOutput;
            const status = policyReview.value?.status;
            // Always show if we have a policy review response (even if parsing failed)
            // This ensures the component doesn't disappear after completion
            return (!!policyReview.value ||
                isProcessing.value ||
                hasError.value ||
                criteriaTree.value.length > 0 ||
                (structuredOutput && Object.keys(structuredOutput).length > 0) ||
                status === "completed");
        });
        const overallStatus = computed(() => {
            const hasFailures = criteriaTree.value.some((c) => {
                const status = getCriterionStatus(c);
                return status === "fail";
            });
            return hasFailures ? "Needs Review" : "Approved";
        });
        // Determine criteria title based on referral status
        const criteriaTitle = computed(() => {
            const referralStatus = policyReview.value?.referralStatus ||
                policyReview.value?.inputs?.initialOrContinuation ||
                props.order?.orderForm?.extraData?.referralStatus ||
                "initial";
            if (referralStatus === "continuation") {
                return "Continuation of Therapy Criteria";
            }
            return "Initial Approval Criteria";
        });
        // Fetch policy review data
        const fetchPolicyReview = async () => {
            loading.value = true;
            try {
                const response = await intakeService.getPolicyReview(props.order.id);
                // Check if response has structured output even if status is processing
                const responseHasData = !!response?.structuredOutput;
                const responseStatus = response?.status;
                // Always update policyReview with the response if it exists
                // This ensures we show errors and completed states
                // CRITICAL: Always update with fresh API data if it has structured output
                // This ensures new data is displayed immediately without requiring a page refresh
                let shouldReparse = false; // Track if we need to re-parse criteria
                // Always update if response has structured output (new data available)
                if (responseHasData) {
                    // Check if this is actually new data by comparing with current data
                    const currentStructuredOutput = policyReview.value?.structuredOutput;
                    const newStructuredOutput = response?.structuredOutput;
                    const pageAttachmentsData = decamelizeKeys(response?.pageAttachments) || {};
                    if (pageAttachmentsData && Object.keys(pageAttachmentsData).length > 0) {
                        pageAttachments.value = { ...pageAttachments.value, ...pageAttachmentsData };
                    }
                    // Compare the data to see if it's different (simple JSON comparison)
                    const isNewData = !currentStructuredOutput || JSON.stringify(currentStructuredOutput) !== JSON.stringify(newStructuredOutput);
                    if (isNewData) {
                        shouldReparse = true; // Mark that we need to re-parse
                        // Clear existing criteria tree to force re-render
                        criteriaTree.value = [];
                    }
                    loading.value = false;
                }
                else if (response.error || responseStatus === "completed" || responseStatus === "failed") {
                    // If response has an error or is completed/failed, always update
                    // This ensures error states are shown
                    loading.value = false;
                }
                policyReview.value = response;
                const structuredOutput = responseHasData ? response?.structuredOutput : policyReview.value?.structuredOutput;
                // Always parse if we have new data from API, or if we don't have criteria yet
                if (shouldReparse || criteriaTree.value.length === 0) {
                    if (structuredOutput?.criteria && Array.isArray(structuredOutput?.criteria)) {
                        criteriaTree.value = [...structuredOutput.criteria];
                        await setCriteriaTree(response, responseHasData);
                        policyReview.value = {
                            ...policyReview.value,
                            structuredOutput: structuredOutput,
                        };
                    }
                }
            }
            catch (error) {
                // Ensure policyReview.value always has a valid structure
                const err = error;
                const errMessage = err.response?.data?.detail || err.message || "Failed to fetch policy review";
                policyReview.value = {
                    status: "failed",
                    error: errMessage,
                    message: errMessage,
                };
            }
            finally {
                loading.value = false;
            }
        };
        const setCriteriaTree = async (response, responseHasData) => {
            // Filter parsed criteria based on referral status
            const referralStatus = (responseHasData ? response : policyReview.value)?.referralStatus ||
                (responseHasData ? response : policyReview.value)?.inputs?.initialOrContinuation ||
                props.order?.orderForm?.extraData?.referralStatus ||
                "initial";
            const filtered = filterCriteriaByReferralStatus(criteriaTree.value, referralStatus);
            // Create new array reference to trigger reactivity
            criteriaTree.value = [...filtered];
            if (criteriaTree.value.length > 0) {
                loading.value = false; // Ensure loading is stopped when we have data
                // Ensure status is set to completed so isProcessing returns false
                // Create a new object to ensure Vue reactivity detects the change
                policyReview.value = {
                    ...policyReview.value,
                    status: "completed",
                };
                // Stop polling immediately when we have data
                if (pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                    pollAttempts = 0;
                }
                // Force immediate reactive update - trigger a re-render
                // This ensures computed properties like isProcessing and shouldShow update
                await nextTick();
                renderKey.value += 1;
            }
        };
        // Filter criteria tree to show only sections relevant to referral status
        const filterCriteriaByReferralStatus = (criteria, referralStatus) => {
            if (!Array.isArray(criteria) || criteria.length === 0) {
                return criteria;
            }
            const normalizedStatus = String(referralStatus).toLowerCase().trim();
            // Define which section names are relevant for each status
            // Note: The actual section names from YAML are "Initial Approval Criteria" and "Continued Therapy Criteria"
            // "Medically Necessary Diagnoses" should always be shown regardless of referral status
            const relevantSections = {
                initial: ["Initial Approval Criteria", "Other Approval Criteria", "Medically Necessary Diagnoses"],
                continuation: ["Continued Therapy Criteria", "Other Approval Criteria", "Medically Necessary Diagnoses"],
            };
            const allowedSections = relevantSections[normalizedStatus] || relevantSections.initial;
            // Filter criteria recursively - sections are top-level items with type "section"
            const filterRecursive = (items, parentSectionName = null) => {
                return items
                    .map((item) => {
                    // Check if this is a section that should be included
                    const sectionName = item.text || item.name || "";
                    const isRelevantSection = allowedSections.some((allowed) => {
                        // Use exact match or case-insensitive contains for section names
                        const sectionLower = sectionName.toLowerCase();
                        const allowedLower = allowed.toLowerCase();
                        return sectionLower === allowedLower || sectionLower.includes(allowedLower);
                    });
                    // Determine the current section name (either this item if it's a section, or inherit from parent)
                    const currentSectionName = item.type === "section" ? sectionName : parentSectionName;
                    // If this item has children, filter them recursively
                    if (item.children && Array.isArray(item.children)) {
                        const filteredChildren = filterRecursive(item.children, currentSectionName);
                        // For sections (top-level items), only include if it's a relevant section
                        if (item.type === "section") {
                            // Top-level section - only include if it matches
                            if (isRelevantSection) {
                                return {
                                    ...item,
                                    children: filteredChildren,
                                };
                            }
                            return null;
                        }
                        else {
                            // Nested group or criterion - include if parent section is relevant
                            // Check if parent section name matches allowed sections
                            const parentIsRelevant = currentSectionName
                                ? allowedSections.some((allowed) => {
                                    const parentLower = currentSectionName.toLowerCase();
                                    const allowedLower = allowed.toLowerCase();
                                    return parentLower === allowedLower || parentLower.includes(allowedLower);
                                })
                                : false;
                            if (parentIsRelevant) {
                                return {
                                    ...item,
                                    children: filteredChildren,
                                };
                            }
                            return null;
                        }
                    }
                    // For leaf nodes (no children), include if parent section is relevant
                    const parentIsRelevant = currentSectionName
                        ? allowedSections.some((allowed) => {
                            const parentLower = currentSectionName.toLowerCase();
                            const allowedLower = allowed.toLowerCase();
                            return parentLower === allowedLower || parentLower.includes(allowedLower);
                        })
                        : false;
                    return parentIsRelevant ? item : null;
                })
                    .filter((item) => item !== null);
            };
            return filterRecursive(criteria);
        };
        const getCriterionStatus = (criterion) => {
            // Check if user has overridden the status
            if (criterion.id && criteriaState.value[criterion.id]) {
                return criteriaState.value[criterion.id];
            }
            // For parent nodes, calculate status from children
            if (criterion.children && criterion.children.length > 0) {
                const childStatuses = criterion.children.map((child) => getCriterionStatus(child));
                if (criterion.logic === "ALL") {
                    return childStatuses.every((s) => s === "pass") ? "pass" : "fail";
                }
                else if (criterion.logic === "ONE") {
                    return childStatuses.some((s) => s === "pass") ? "pass" : "fail";
                }
            }
            return criterion.status || "pending";
        };
        const onStatusChange = async (criterionId, newStatus) => {
            if (!criterionId)
                return;
            // Update local state immediately for responsive UI
            criteriaState.value[criterionId] = newStatus;
            // Update in backend
            try {
                await intakeService.updatePolicyReview(props.order.id, {
                    criteria_state: {
                        [criterionId]: newStatus,
                    },
                });
            }
            catch (error) {
                // Error updating criteria status
                const err = error;
                // Revert on error
                delete criteriaState.value[criterionId];
                // Show user-friendly error message
                if (err.response?.data?.errorDetails) {
                    alert(`Failed to update status: ${err.response.data.errorDetails}`);
                }
                else if (err.message) {
                    alert(`Failed to update status: ${err.message}`);
                }
            }
        };
        const retryPolicyReview = async () => {
            if (retryingPolicyReview.value)
                return;
            retryingPolicyReview.value = true;
            loading.value = true;
            criteriaTree.value = [];
            try {
                const response = await intakeService.triggerPolicyReview(props.order.id);
                // Ensure response exists before using it
                if (!response) {
                    throw new Error("No response received from policy review trigger");
                }
                policyReview.value = response;
            }
            catch (error) {
                // Extract detailed error message from response
                const err = error;
                const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to trigger policy review";
                policyReview.value = {
                    status: "failed",
                    error: errorMessage,
                    message: errorMessage,
                };
            }
            finally {
                retryingPolicyReview.value = false;
                loading.value = false;
            }
        };
        const onPageAction = async (criterionId, action, pageId) => {
            if (!criterionId)
                return;
            const numericPageId = typeof pageId === "string" ? parseInt(pageId, 10) : pageId;
            if (action === "add" && numericPageId) {
                if (!pageAttachments.value[criterionId]) {
                    pageAttachments.value[criterionId] = [];
                }
                if (!pageAttachments.value[criterionId].includes(numericPageId)) {
                    pageAttachments.value[criterionId].push(numericPageId);
                }
            }
            else if (action === "remove" && numericPageId) {
                if (pageAttachments.value[criterionId]) {
                    pageAttachments.value[criterionId] = pageAttachments.value[criterionId].filter((id) => id !== numericPageId);
                }
            }
            // Update in backend
            try {
                await intakeService.updatePolicyReview(props.order.id, {
                    page_attachments: {
                        [criterionId]: pageAttachments.value[criterionId] || [],
                    },
                });
            }
            catch (error) {
                // Error updating page attachments
                const err = error;
                // Show user-friendly error message
                if (err.response?.data?.errorDetails) {
                    alert(`Failed to update page: ${err.response.data.errorDetails}`);
                }
                else if (err.message) {
                    alert(`Failed to update page: ${err.message}`);
                }
            }
        };
        // Poll for updates if processing (but don't poll if we have completed data)
        // Watch both isProcessing and policyReview status to ensure we poll when needed
        watch([() => isProcessing.value, () => policyReview.value?.status], ([processing, status]) => {
            // Check if we have truly completed data (status is completed AND we have structured output)
            const hasCompletedData = status === "completed" && policyReview.value?.structuredOutput && criteriaTree.value.length > 0;
            // Start polling if:
            // 1. Status is processing OR we're in processing state
            // 2. We don't already have a poll interval running
            // 3. We don't have truly completed data
            const shouldPoll = (processing || status === "processing") && !pollInterval && !hasCompletedData;
            if (shouldPoll) {
                pollAttempts = 0; // Reset attempts when starting new polling
                pollInterval = setInterval(() => {
                    pollAttempts++;
                    // Stop polling if we've exceeded max attempts
                    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
                        if (pollInterval) {
                            clearInterval(pollInterval);
                            pollInterval = null;
                        }
                        // Set status to failed with timeout message
                        // Safely check and update status
                        if (policyReview.value && typeof policyReview.value === "object") {
                            const currentStatus = policyReview.value.status;
                            if (currentStatus === "processing") {
                                policyReview.value.status = "failed";
                                policyReview.value.error =
                                    "Policy review is taking longer than expected (over 5 minutes). The review may still be processing. Please refresh the page or contact support if this persists.";
                            }
                        }
                        loading.value = false;
                        return;
                    }
                    // Always fetch to get the latest status from the API
                    // This ensures we detect completion as soon as it happens
                    fetchPolicyReview();
                }, POLL_INTERVAL_MS);
            }
            else if (!processing && status === "completed" && pollInterval) {
                // Stop polling if we're no longer processing and status is completed
                clearInterval(pollInterval);
                pollInterval = null;
                pollAttempts = 0;
            }
        }, { immediate: true } // Run immediately to check initial state
        );
        // Provide helper functions to child components
        provide("getCriterionStatus", getCriterionStatus);
        provide("getPageAttachments", (id) => pageAttachments.value[id] || []);
        onMounted(() => {
            fetchPolicyReview();
        });
        onUnmounted(() => {
            // Clean up polling interval when component is unmounted
            if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
            }
            pollAttempts = 0;
        });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_divider = _resolveComponent("v-divider");
            const _component_v_expansion_panel_title = _resolveComponent("v-expansion-panel-title");
            const _component_v_expansion_panel_text = _resolveComponent("v-expansion-panel-text");
            const _component_v_expansion_panel = _resolveComponent("v-expansion-panel");
            const _component_v_expansion_panels = _resolveComponent("v-expansion-panels");
            const _component_v_card = _resolveComponent("v-card");
            return (shouldShow.value)
                ? (_openBlock(), _createBlock(_component_v_card, {
                    key: renderKey.value,
                    class: "mt-4 mb-4 policy-review-criteria-card",
                    color: "surface",
                    variant: "elevated",
                    elevation: "0"
                }, {
                    default: _withCtx(() => [
                        _createVNode(_component_v_card_title, { class: "d-flex align-center gap-2" }, {
                            default: _withCtx(() => [
                                _createVNode(_component_v_icon, {
                                    icon: "mdi-scroll-text",
                                    color: "primary",
                                    size: "large"
                                }),
                                _createElementVNode("h2", _hoisted_1, _toDisplayString(criteriaTitle.value), 1)
                            ]),
                            _: 1
                        }),
                        (isProcessing.value)
                            ? (_openBlock(), _createBlock(_component_v_card_text, { key: 0 }, {
                                default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_2, [
                                        _createVNode(_component_v_progress_circular, {
                                            indeterminate: "",
                                            color: "primary",
                                            size: "24"
                                        }),
                                        _cache[0] || (_cache[0] = _createElementVNode("span", { class: "text-body-1" }, "Policy review is running...", -1))
                                    ])
                                ]),
                                _: 1
                            }))
                            : (hasError.value)
                                ? (_openBlock(), _createBlock(_component_v_card_text, { key: 1 }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_alert, {
                                            type: "error",
                                            variant: "outlined",
                                            class: "mb-0"
                                        }, {
                                            default: _withCtx(() => [
                                                _cache[2] || (_cache[2] = _createElementVNode("div", { class: "text-body-1 font-weight-semibold mb-2" }, "Policy Review Failed", -1)),
                                                _createElementVNode("div", _hoisted_3, _toDisplayString(errorMessage.value), 1),
                                                (canRetryPolicyReview.value)
                                                    ? (_openBlock(), _createBlock(_component_v_btn, {
                                                        key: 0,
                                                        size: "small",
                                                        color: "primary",
                                                        variant: "outlined",
                                                        loading: retryingPolicyReview.value,
                                                        onClick: retryPolicyReview
                                                    }, {
                                                        default: _withCtx(() => _cache[1] || (_cache[1] = [
                                                            _createTextVNode(" Retry Policy Review ", -1)
                                                        ])),
                                                        _: 1,
                                                        __: [1]
                                                    }, 8, ["loading"]))
                                                    : _createCommentVNode("", true)
                                            ]),
                                            _: 1,
                                            __: [2]
                                        })
                                    ]),
                                    _: 1
                                }))
                                : (criteriaTree.value.length > 0)
                                    ? (_openBlock(), _createBlock(_component_v_card_text, { key: 2 }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_4, [
                                                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "d-flex align-center gap-2" }, [
                                                    _createElementVNode("div", { class: "h-3 w-3 rounded-full bg-success" }),
                                                    _createElementVNode("span", { class: "text-muted-foreground" }, "Present")
                                                ], -1)),
                                                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "d-flex align-center gap-2" }, [
                                                    _createElementVNode("div", { class: "h-3 w-3 rounded-full bg-error" }),
                                                    _createElementVNode("span", { class: "text-muted-foreground" }, "Missing")
                                                ], -1)),
                                                _createVNode(_component_v_divider, {
                                                    vertical: "",
                                                    class: "h-4"
                                                }),
                                                _createElementVNode("div", _hoisted_5, [
                                                    _cache[3] || (_cache[3] = _createTextVNode(" Status: ", -1)),
                                                    _createElementVNode("span", {
                                                        class: _normalizeClass(overallStatus.value === 'Approved' ? 'text-success' : 'text-error')
                                                    }, _toDisplayString(overallStatus.value), 3)
                                                ])
                                            ]),
                                            _createElementVNode("div", _hoisted_6, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criteriaTree.value, (criterion) => {
                                                    return (_openBlock(), _createElementBlock(_Fragment, {
                                                        key: criterion.id
                                                    }, [
                                                        (criterion.text === 'Medically Necessary Diagnoses' && criterion.children)
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                _createVNode(CriteriaItem, {
                                                                    criterion: { ...criterion, children: [] },
                                                                    "current-page-id": _ctx.currentPageId,
                                                                    "page-manager": _ctx.pageManager,
                                                                    status: getCriterionStatus(criterion),
                                                                    "attached-pages": getAttachedPages(criterion.id),
                                                                    onStatusChange: onStatusChange,
                                                                    onPageAction: onPageAction
                                                                }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]),
                                                                _createElementVNode("div", _hoisted_7, [
                                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criterion.children.filter((c) => getCriterionStatus(c) === 'pass'), (child) => {
                                                                        return (_openBlock(), _createBlock(CriteriaItem, {
                                                                            key: child.id,
                                                                            criterion: child,
                                                                            "current-page-id": _ctx.currentPageId,
                                                                            "page-manager": _ctx.pageManager,
                                                                            status: getCriterionStatus(child),
                                                                            "attached-pages": getAttachedPages(child.id),
                                                                            onStatusChange: onStatusChange,
                                                                            onPageAction: onPageAction
                                                                        }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]));
                                                                    }), 128)),
                                                                    (criterion.children.filter((c) => getCriterionStatus(c) !== 'pass').length > 0)
                                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                                                            _createVNode(_component_v_expansion_panels, {
                                                                                variant: "accordion",
                                                                                class: "medically-necessary-accordion"
                                                                            }, {
                                                                                default: _withCtx(() => [
                                                                                    _createVNode(_component_v_expansion_panel, null, {
                                                                                        default: _withCtx(() => [
                                                                                            _createVNode(_component_v_expansion_panel_title, { class: "text-body-2 text-center" }, {
                                                                                                default: _withCtx(() => _cache[6] || (_cache[6] = [
                                                                                                    _createTextVNode(" View full list of diagnoses ", -1)
                                                                                                ])),
                                                                                                _: 1,
                                                                                                __: [6]
                                                                                            }),
                                                                                            _createVNode(_component_v_expansion_panel_text, null, {
                                                                                                default: _withCtx(() => [
                                                                                                    _createElementVNode("div", _hoisted_9, [
                                                                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criterion.children.filter((c) => getCriterionStatus(c) !== 'pass'), (child) => {
                                                                                                            return (_openBlock(), _createBlock(CriteriaItem, {
                                                                                                                key: child.id,
                                                                                                                criterion: child,
                                                                                                                "current-page-id": _ctx.currentPageId,
                                                                                                                "page-manager": _ctx.pageManager,
                                                                                                                status: getCriterionStatus(child),
                                                                                                                "attached-pages": getAttachedPages(child.id),
                                                                                                                onStatusChange: onStatusChange,
                                                                                                                onPageAction: onPageAction
                                                                                                            }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]));
                                                                                                        }), 128))
                                                                                                    ])
                                                                                                ]),
                                                                                                _: 2
                                                                                            }, 1024)
                                                                                        ]),
                                                                                        _: 2
                                                                                    }, 1024)
                                                                                ]),
                                                                                _: 2
                                                                            }, 1024)
                                                                        ]))
                                                                        : _createCommentVNode("", true)
                                                                ])
                                                            ], 64))
                                                            : (_openBlock(), _createBlock(CriteriaItem, {
                                                                key: 1,
                                                                criterion: criterion,
                                                                "current-page-id": _ctx.currentPageId,
                                                                "page-manager": _ctx.pageManager,
                                                                status: getCriterionStatus(criterion),
                                                                "attached-pages": getAttachedPages(criterion.id),
                                                                onStatusChange: onStatusChange,
                                                                onPageAction: onPageAction
                                                            }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]))
                                                    ], 64));
                                                }), 128))
                                            ]),
                                            _createElementVNode("div", _hoisted_10, [
                                                (canRetryPolicyReview.value)
                                                    ? (_openBlock(), _createBlock(_component_v_btn, {
                                                        key: 0,
                                                        size: "small",
                                                        color: "primary",
                                                        variant: "outlined",
                                                        loading: retryingPolicyReview.value,
                                                        onClick: retryPolicyReview
                                                    }, {
                                                        default: _withCtx(() => _cache[7] || (_cache[7] = [
                                                            _createTextVNode(" Retry Policy Review ", -1)
                                                        ])),
                                                        _: 1,
                                                        __: [7]
                                                    }, 8, ["loading"]))
                                                    : _createCommentVNode("", true)
                                            ])
                                        ]),
                                        _: 1
                                    }))
                                    : (policyReview.value?.structuredOutput?.textInsideCriteriaDropdown && criteriaTree.value.length === 0)
                                        ? (_openBlock(), _createBlock(_component_v_card_text, { key: 3 }, {
                                            default: _withCtx(() => [
                                                _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-body-2 mb-2" }, [
                                                    _createElementVNode("strong", null, "Policy Criteria:")
                                                ], -1)),
                                                _createVNode(_component_v_expansion_panels, null, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_component_v_expansion_panel, null, {
                                                            default: _withCtx(() => [
                                                                _createVNode(_component_v_expansion_panel_title, null, {
                                                                    default: _withCtx(() => [
                                                                        _createTextVNode(_toDisplayString(policyReview.value?.structuredOutput?.textHeaderCriteriaDropdown || "Policy Criteria"), 1)
                                                                    ]),
                                                                    _: 1
                                                                }),
                                                                _createVNode(_component_v_expansion_panel_text, null, {
                                                                    default: _withCtx(() => [
                                                                        _createElementVNode("div", _hoisted_11, _toDisplayString(policyReview.value?.structuredOutput?.textInsideCriteriaDropdown || ""), 1)
                                                                    ]),
                                                                    _: 1
                                                                })
                                                            ]),
                                                            _: 1
                                                        })
                                                    ]),
                                                    _: 1
                                                })
                                            ]),
                                            _: 1,
                                            __: [8]
                                        }))
                                        : (_openBlock(), _createBlock(_component_v_card_text, { key: 4 }, {
                                            default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_12, [
                                                    (policyReview.value?.status === 'completed')
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, " Policy review completed, but no criteria data is available. The review may have completed without generating criteria, or there may be an issue with the data format. "))
                                                        : (_openBlock(), _createElementBlock("div", _hoisted_14, "No approval criteria available. Policy review may still be processing."))
                                                ])
                                            ]),
                                            _: 1
                                        }))
                    ]),
                    _: 1
                }))
                : _createCommentVNode("", true);
        };
    }
});
